import type { JSX } from 'react';
import { cn } from '../lib/cn.js';

/**
 * LA PRIMITIVE POPOVER DES OUTILS (gabarit v2, zone 3b).
 *
 * UN seul composant pour le popover de FILTRE et le popover de TRI : les deux
 * outils partagent le meme panneau, le meme item de 2rem et la meme entree de
 * sortie. Le prototype v2 portait un `.sortsel` complet (bouton a libelle +
 * menu) : c'est du code MORT, remplace par `.toolpop` dans la variante v2d
 * retenue ; il n'est volontairement PAS repris ici.
 *
 * Un popover est un objet de SHELL, au meme titre que le Modal : il reprend
 * son vocabulaire (--shadow-pop, --radius-md) et se pose PAR-DESSUS la zone 5.
 * L'interdit des encadres arrondis (G9) porte sur la zone 5, pas sur un menu
 * pose au-dessus.
 *
 * Ce composant est VOLONTAIREMENT muet sur son ouverture : l'etat ouvert /
 * ferme, l'ancrage (un parent `position: relative`) et la fermeture (Escape,
 * clic dehors) appartiennent a l'appelant (ScreenLayout pour le gabarit).
 * Un item clique ne ferme rien : les deux outils sont MULTI-CHOIX, on pose
 * plusieurs filtres ou plusieurs niveaux de tri sans rouvrir le menu.
 *
 * Anatomie d'un item : point d'encre (visible quand actif), libelle, detail
 * optionnel a droite en mono (compteur d'un filtre, "niveau n" d'un tri).
 * L'actif se marque a l'ENCRE (point + graisse), jamais en lime : le lime
 * reste le seul bouton d'ajout de la rangee 3a.
 */

/** Une entree du popover. */
export interface ToolPopoverItem {
  /** Id stable, rendu a `onPick` tel quel. */
  id: string;
  /** Libelle affiche. */
  label: string;
  /** Entree active : point d'encre plein + graisse 600 + aria-selected. */
  active: boolean;
  /**
   * Detail optionnel a droite, en mono --ink-400 : le compteur d'un filtre
   * ("12"), le rang d'un niveau de tri ("niveau 2"). Absent : rien.
   */
  detail?: string;
}

export interface ToolPopoverProps {
  /** Id DOM du panneau (cible de l'aria-controls du bouton d'outil). */
  id?: string;
  /** Libelle du listbox (aria-label) : "Filtrer" ou "Trier". */
  label: string;
  /** Les entrees, dans l'ordre du catalogue. */
  items: ToolPopoverItem[];
  /** Un clic d'item : pose ou retire (multi-choix). Le popover reste ouvert. */
  onPick: (id: string) => void;
  /**
   * L'ENTREE DE SORTIE, sous un filet : revenir a l'etat de base sans retirer
   * les choix un par un ("Tout afficher", "Ordre d'origine"). Ce n'est pas un
   * choix de plus : elle est grisee et inerte quand il n'y a rien a vider.
   */
  clearLabel: string;
  /** Rien a vider : l'entree de sortie est desactivee. */
  clearDisabled?: boolean;
  /** Le clic de l'entree de sortie. */
  onClear: () => void;
}

/**
 * Rend le panneau, ancre sous le bord droit de son parent `relative`
 * (top 100% + 0.4375rem, comme le proto v2d). L'appelant le monte quand il
 * est ouvert et le demonte sinon.
 */
export function ToolPopover({
  id,
  label,
  items,
  onPick,
  clearLabel,
  clearDisabled = false,
  onClear,
}: ToolPopoverProps): JSX.Element {
  return (
    <div
      {...(id != null ? { id } : {})}
      role="listbox"
      aria-label={label}
      className={cn(
        'absolute right-0 top-[calc(100%+0.4375rem)] z-20 flex min-w-48 flex-col p-1',
        'rounded-md border border-border bg-surface shadow-pop',
      )}
    >
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          role="option"
          aria-selected={item.active}
          onClick={() => onPick(item.id)}
          className={cn(
            'flex h-8 w-full cursor-pointer items-center gap-2 rounded-sm px-2 text-left',
            'font-sans text-[0.8125rem] font-medium text-ink-700 transition-colors',
            'hover:bg-surface-1 hover:text-ink-900',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border',
            item.active && 'font-semibold text-ink-900',
          )}
        >
          {/* Le point d'encre : toujours rendu, visible quand actif. Sa boite
              reserve la place, les libelles restent alignes actif ou pas. */}
          <span
            aria-hidden
            className={cn(
              'h-1.5 w-1.5 shrink-0 rounded-pill bg-ink-900',
              item.active ? 'opacity-100' : 'opacity-0',
            )}
          />
          <span className="min-w-0 flex-1 truncate">{item.label}</span>
          {item.detail != null && (
            <span className="shrink-0 pl-3 font-mono text-[0.6875rem] font-medium text-ink-400">
              {item.detail}
            </span>
          )}
        </button>
      ))}
      <button
        type="button"
        disabled={clearDisabled}
        onClick={onClear}
        className={cn(
          'mt-1 h-8 w-full cursor-pointer border-t border-border-soft bg-transparent pl-2 pt-[0.4375rem] text-left',
          'font-sans text-[0.8125rem] font-medium text-ink-500 transition-colors hover:text-ink-900',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border',
          'disabled:cursor-default disabled:text-ink-300',
        )}
      >
        {clearLabel}
      </button>
    </div>
  );
}

export default ToolPopover;
